import type { ReactNode } from "react";

export type IconMode = "inline" | "chip" | "none";

export interface OptionStyle {
  /** Gradient border wrapper (p-px) — used by ghost/outlined variants */
  wrapperClass?: string;
  /** Class list for a solid/gradient fill button */
  btnClass?: string;
  /** Class list for the inner button of a ghost variant */
  innerClass?: string;
  /** Optional text override (e.g. gradient text) */
  labelClass?: string;
  /** Chip fill for the chip-icon variant */
  chipClass?: string;
  icon: IconMode;
}

export interface CTAOptionDef {
  id: number;
  name: string;
  note: string;
  light: OptionStyle;
  dark: OptionStyle;
}

interface CTAOptionButtonProps {
  option: CTAOptionDef;
  theme: "light" | "dark";
  fullWidth?: boolean;
  disabled?: boolean;
}

function FocusClasses(theme: "light" | "dark") {
  return theme === "dark"
    ? "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F172A] focus-visible:ring-[#3B82F6]"
    : "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:ring-[#2563EB]";
}

function buildBase(theme: "light" | "dark", inline: boolean, fullWidth: boolean) {
  return [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold transition-all duration-300",
    inline ? "btn-arrow-hover" : "",
    FocusClasses(theme),
    "active:translate-y-0 active:scale-[0.98]",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none disabled:saturate-0 disabled:brightness-100",
    fullWidth ? "w-full" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export function CTAOptionButton({
  option,
  theme,
  fullWidth = false,
  disabled = false,
}: CTAOptionButtonProps) {
  const style = theme === "dark" ? option.dark : option.light;
  const base = buildBase(theme, style.icon === "inline", fullWidth);
  const label: ReactNode = (
    <span className={style.labelClass || ""}>Let&apos;s Talk</span>
  );

  let icon: ReactNode = null;
  if (style.icon === "inline") {
    icon = <i className="ri-arrow-right-line text-xs btn-arrow" />;
  } else if (style.icon === "chip") {
    icon = (
      <span
        className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${style.chipClass || ""}`}
      >
        <i className="ri-arrow-right-line text-xs" />
      </span>
    );
  }

  // Ghost / outlined variant with a gradient border wrapper
  if (style.wrapperClass) {
    return (
      <span className={`inline-flex ${fullWidth ? "w-full" : ""} ${style.wrapperClass}`}>
        <a
          href="#"
          onClick={(e) => e.preventDefault()}
          aria-disabled={disabled}
          className={`${base} ${fullWidth ? "w-full" : ""} ${style.innerClass || ""}`}
        >
          {label}
          {icon}
        </a>
      </span>
    );
  }

  return (
    <a
      href="#"
      onClick={(e) => e.preventDefault()}
      aria-disabled={disabled}
      className={`${base} ${fullWidth ? "w-full" : ""} ${style.btnClass || ""}`}
    >
      {label}
      {icon}
    </a>
  );
}

/**
 * 8 brand-derived CTA alternatives.
 * All share: label "Let's Talk", same padding scale (px-5 py-2.5),
 * Inter text-sm font-semibold, WCAG AA text contrast, and defined
 * hover / active / focus-visible / disabled states.
 */
export const ctaOptionDefs: CTAOptionDef[] = [
  {
    id: 1,
    name: "Brand Gradient Pill",
    note: "3-color brand gradient, full pill, inline arrow, soft purple glow.",
    light: {
      btnClass:
        "bg-gradient-to-r from-[#2563EB] via-[#6D28D9] to-[#C2410C] text-white rounded-full shadow-lg shadow-[#7C3AED]/25 hover:brightness-110 hover:-translate-y-0.5",
      icon: "inline",
    },
    dark: {
      btnClass:
        "bg-gradient-to-r from-[#2563EB] via-[#6D28D9] to-[#C2410C] text-white rounded-full shadow-lg shadow-black/30 hover:brightness-110 hover:-translate-y-0.5 ring-1 ring-white/10",
      icon: "inline",
    },
  },
  {
    id: 2,
    name: "Solid Navy · Minimal",
    note: "Solid deep-navy fill, slightly rounded, no icon — understated premium.",
    light: {
      btnClass:
        "bg-[#0F172A] text-white rounded-lg hover:bg-[#1e293b] hover:-translate-y-0.5 shadow-lg shadow-[#0F172A]/20",
      icon: "none",
    },
    dark: {
      btnClass:
        "bg-[#334155] text-white rounded-lg hover:bg-[#475569] hover:-translate-y-0.5 border border-white/10",
      icon: "none",
    },
  },
  {
    id: 3,
    name: "Solid Blue · Sharp",
    note: "Solid accent blue, sharp corners, inline arrow — bold and direct.",
    light: {
      btnClass:
        "bg-[#2563EB] text-white rounded-[5px] hover:bg-[#1d4ed8] hover:-translate-y-0.5 shadow-lg shadow-[#2563EB]/25",
      icon: "inline",
    },
    dark: {
      btnClass:
        "bg-[#2563EB] text-white rounded-[5px] hover:bg-[#3B82F6] hover:-translate-y-0.5 shadow-lg shadow-black/30",
      icon: "inline",
    },
  },
  {
    id: 4,
    name: "Gradient Ghost · Pill",
    note: "Ghost outline with gradient border + gradient text, pill, no icon.",
    light: {
      wrapperClass:
        "bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#C2410C] rounded-full p-px hover:brightness-110",
      innerClass:
        "bg-background-50 text-[#7C3AED] rounded-full hover:bg-background-100",
      labelClass:
        "text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#C2410C]",
      icon: "none",
    },
    dark: {
      wrapperClass:
        "bg-gradient-to-r from-[#60A5FA] via-[#C4B5FD] to-[#FDBA74] rounded-full p-px hover:brightness-110",
      innerClass:
        "bg-[#0F172A] text-[#C4B5FD] rounded-full hover:bg-[#1e293b]",
      labelClass:
        "text-transparent bg-clip-text bg-gradient-to-r from-[#60A5FA] via-[#C4B5FD] to-[#FDBA74]",
      icon: "none",
    },
  },
  {
    id: 5,
    name: "Gradient + Chip Arrow",
    note: "Gradient fill, slightly rounded, arrow in a circular translucent chip.",
    light: {
      btnClass:
        "bg-gradient-to-r from-[#2563EB] via-[#6D28D9] to-[#C2410C] text-white rounded-xl hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-[#7C3AED]/25",
      chipClass: "bg-white/25",
      icon: "chip",
    },
    dark: {
      btnClass:
        "bg-gradient-to-r from-[#2563EB] via-[#6D28D9] to-[#C2410C] text-white rounded-xl hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-black/30 ring-1 ring-white/10",
      chipClass: "bg-white/25",
      icon: "chip",
    },
  },
  {
    id: 6,
    name: "Purple → Orange Elevated",
    note: "Two-color secondary→orange gradient, soft elevated shadow, rounded.",
    light: {
      btnClass:
        "bg-gradient-to-r from-[#7C3AED] to-[#C2410C] text-white rounded-lg shadow-lg shadow-[#F97316]/25 hover:brightness-110 hover:-translate-y-1",
      icon: "inline",
    },
    dark: {
      btnClass:
        "bg-gradient-to-r from-[#7C3AED] to-[#C2410C] text-white rounded-lg shadow-lg shadow-black/30 hover:brightness-110 hover:-translate-y-1 ring-1 ring-white/10",
      icon: "inline",
    },
  },
  {
    id: 7,
    name: "Soft Elevated Ghost",
    note: "Elevated light card, thin colored border + colored text, sharp, arrow.",
    light: {
      btnClass:
        "bg-white border border-[#2563EB]/30 text-[#2563EB] rounded-md shadow-lg shadow-[#2563EB]/10 hover:border-[#2563EB]/60 hover:bg-[#2563EB]/5 hover:-translate-y-0.5",
      icon: "inline",
    },
    dark: {
      btnClass:
        "bg-white/5 border border-white/25 text-white rounded-md shadow-lg shadow-black/20 hover:bg-white/10 hover:border-white/40 hover:-translate-y-0.5",
      icon: "inline",
    },
  },
  {
    id: 8,
    name: "Gradient Chip · Ghost Pill",
    note: "Ghost pill with gradient text + circular gradient arrow chip, no inline arrow.",
    light: {
      wrapperClass:
        "bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#C2410C] rounded-full p-px hover:brightness-110",
      innerClass:
        "bg-background-50 text-[#2563EB] rounded-full hover:bg-background-100",
      labelClass:
        "text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#C2410C]",
      chipClass:
        "bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#C2410C]",
      icon: "chip",
    },
    dark: {
      wrapperClass:
        "bg-gradient-to-r from-[#60A5FA] via-[#C4B5FD] to-[#FDBA74] rounded-full p-px hover:brightness-110",
      innerClass:
        "bg-[#0F172A] text-[#C4B5FD] rounded-full hover:bg-[#1e293b]",
      labelClass:
        "text-transparent bg-clip-text bg-gradient-to-r from-[#60A5FA] via-[#C4B5FD] to-[#FDBA74]",
      chipClass:
        "bg-gradient-to-r from-[#60A5FA] via-[#C4B5FD] to-[#FDBA74]",
      icon: "chip",
    },
  },
];

/** Additional color palette options beyond the brand gradient. */
export const ctaColorOptionDefs: CTAOptionDef[] = [
  {
    id: 9,
    name: "Emerald → Teal Gradient",
    note: "Fresh green-to-teal gradient, rounded-xl, chip arrow — nature + tech vibe.",
    light: {
      btnClass:
        "bg-gradient-to-r from-[#059669] to-[#0D9488] text-white rounded-xl hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-[#059669]/25",
      chipClass: "bg-white/30",
      icon: "chip",
    },
    dark: {
      btnClass:
        "bg-gradient-to-r from-[#10B981] to-[#14B8A6] text-white rounded-xl hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-black/30 ring-1 ring-white/10",
      chipClass: "bg-white/30",
      icon: "chip",
    },
  },
  {
    id: 10,
    name: "Crimson → Rose Gradient",
    note: "Deep red to rose pink gradient, pill shape, inline arrow — bold and energetic.",
    light: {
      btnClass:
        "bg-gradient-to-r from-[#DC2626] to-[#E11D48] text-white rounded-full hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-[#DC2626]/25",
      icon: "inline",
    },
    dark: {
      btnClass:
        "bg-gradient-to-r from-[#EF4444] to-[#F43F5E] text-white rounded-full hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-black/30 ring-1 ring-white/10",
      icon: "inline",
    },
  },
  {
    id: 11,
    name: "Amber → Yellow Gradient",
    note: "Warm amber to yellow gradient, rounded-lg, inline arrow — friendly and inviting.",
    light: {
      btnClass:
        "bg-gradient-to-r from-[#D97706] to-[#CA8A04] text-white rounded-lg hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-[#D97706]/25",
      icon: "inline",
    },
    dark: {
      btnClass:
        "bg-gradient-to-r from-[#F59E0B] to-[#EAB308] text-white rounded-lg hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-black/30 ring-1 ring-white/10",
      icon: "inline",
    },
  },
  {
    id: 12,
    name: "Cyan → Sky Gradient",
    note: "Bright cyan to sky blue gradient, rounded-xl, chip arrow — clean and modern.",
    light: {
      btnClass:
        "bg-gradient-to-r from-[#0891B2] to-[#0284C7] text-white rounded-xl hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-[#0891B2]/25",
      chipClass: "bg-white/30",
      icon: "chip",
    },
    dark: {
      btnClass:
        "bg-gradient-to-r from-[#06B6D4] to-[#38BDF8] text-white rounded-xl hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-black/30 ring-1 ring-white/10",
      chipClass: "bg-white/30",
      icon: "chip",
    },
  },
  {
    id: 13,
    name: "Solid Emerald · Minimal",
    note: "Solid emerald green, slightly rounded, no icon — fresh and trustworthy.",
    light: {
      btnClass:
        "bg-[#059669] text-white rounded-lg hover:bg-[#047857] hover:-translate-y-0.5 shadow-lg shadow-[#059669]/20",
      icon: "none",
    },
    dark: {
      btnClass:
        "bg-[#10B981] text-white rounded-lg hover:bg-[#34D399] hover:-translate-y-0.5 border border-white/10",
      icon: "none",
    },
  },
  {
    id: 14,
    name: "Rose Ghost · Outline",
    note: "Ghost outline with rose border + rose text, sharp corners, inline arrow.",
    light: {
      btnClass:
        "bg-white border border-[#E11D48]/40 text-[#E11D48] rounded-[5px] shadow-lg shadow-[#E11D48]/10 hover:border-[#E11D48]/70 hover:bg-[#E11D48]/5 hover:-translate-y-0.5",
      icon: "inline",
    },
    dark: {
      btnClass:
        "bg-white/5 border border-[#F43F5E]/40 text-[#F43F5E] rounded-[5px] shadow-lg shadow-black/20 hover:bg-white/10 hover:border-[#F43F5E]/70 hover:-translate-y-0.5",
      icon: "inline",
    },
  },
  {
    id: 15,
    name: "Monochrome Black · White",
    note: "Solid black fill, white text, sharp corners, inline arrow — classic and bold.",
    light: {
      btnClass:
        "bg-[#18181B] text-white rounded-[5px] hover:bg-[#27272A] hover:-translate-y-0.5 shadow-lg shadow-[#18181B]/20",
      icon: "inline",
    },
    dark: {
      btnClass:
        "bg-white text-[#18181B] rounded-[5px] hover:bg-gray-100 hover:-translate-y-0.5 shadow-lg shadow-black/30",
      icon: "inline",
    },
  },
  {
    id: 16,
    name: "Pink → Violet Gradient",
    note: "Playful pink to violet gradient, full pill, chip arrow — creative and fun.",
    light: {
      btnClass:
        "bg-gradient-to-r from-[#DB2777] to-[#7C3AED] text-white rounded-full hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-[#DB2777]/25",
      chipClass: "bg-white/25",
      icon: "chip",
    },
    dark: {
      btnClass:
        "bg-gradient-to-r from-[#EC4899] to-[#A78BFA] text-white rounded-full hover:brightness-110 hover:-translate-y-0.5 shadow-lg shadow-black/30 ring-1 ring-white/10",
      chipClass: "bg-white/25",
      icon: "chip",
    },
  },
];